import * as React from "react";
import { useContext } from "react";
import { makeStyles, createStyles, Theme, Grid, Typography, Button } from "@material-ui/core";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { Link } from "gatsby";
import ReactMarkdown from "react-markdown";
import Section from "../Section";
import { LocaleContext } from "../../context/LocaleContext";
import { SECTION_NAVY, SECTION_PADDING_Y } from "../../styles/homepageSections";

export type BottomCtaData = {
  subheadline?: string | null;
  title?: string | null;
  description?: string | null;
  buttonText?: string | null;
  url?: string | null;
  image?: {
    localFile?: {
      childImageSharp?: { gatsbyImageData: IGatsbyImageData };
    };
  } | null;
};

const hasText = (value?: string | null) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    section: {
      padding: SECTION_PADDING_Y.mobile,
      color: "#fff",
      [t.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y.desktop,
      },
    },
    subheadline: {
      color: t.palette.primary.main,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      marginBottom: 12,
      [t.breakpoints.up("md")]: {
        fontSize: 13,
        marginBottom: 16,
      },
    },
    headline: {
      color: "#fff",
      fontWeight: 600,
      fontSize: 32,
      lineHeight: 1.25,
      margin: 0,
      padding: "0 0 1.5rem !important",
      [t.breakpoints.up("md")]: {
        fontSize: 44,
        lineHeight: 1.2,
        paddingBottom: "2rem !important",
      },
    },
    headlineAfterSubheadline: {
      paddingTop: "0 !important",
      marginTop: 0,
    },
    description: {
      color: "rgba(255, 255, 255, 0.85)",
      fontSize: 17,
      lineHeight: 1.55,
      marginBottom: 32,
      "& p": {
        margin: "0 0 1em",
      },
      "& p:last-child": {
        marginBottom: 0,
      },
      "& strong": {
        color: "#fff",
        fontWeight: 600,
      },
      "& ul": {
        margin: "1em 0 0",
        padding: 0,
        listStyle: "none",
      },
      "& li": {
        position: "relative",
        paddingLeft: 20,
        marginBottom: 12,
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: "0.55em",
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: t.palette.primary.main,
        },
      },
      [t.breakpoints.up("md")]: {
        fontSize: 18,
        lineHeight: 1.6,
      },
    },
    ctaLink: {
      textDecoration: "none",
      display: "inline-block",
    },
    ctaButton: {
      minHeight: 52,
      padding: "14px 32px",
      fontSize: 16,
      fontWeight: 600,
      textTransform: "none",
      color: "#fff",
      backgroundColor: t.palette.primary.main,
      borderRadius: 4,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: t.palette.primary.main,
        opacity: 0.92,
        boxShadow: "none",
      },
    },
    imagePanel: {
      borderRadius: 12,
      overflow: "hidden",
      backgroundColor: "#eef0f3",
      minHeight: 280,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      [t.breakpoints.up("md")]: {
        minHeight: 360,
      },
    },
    image: {
      width: "100%",
      display: "block",
    },
    imageOrder: {
      [t.breakpoints.down("sm")]: {
        order: -1,
        marginBottom: 32,
      },
    },
  })
);

const BottomCtaSection: React.FC<{ data?: BottomCtaData | null }> = ({ data }) => {
  const classes = useStyles();
  const { locale } = useContext(LocaleContext);

  if (!data || !hasText(data.title)) {
    return null;
  }

  const gatsbyImage = data.image?.localFile?.childImageSharp?.gatsbyImageData;
  const href = data.url
    ? data.url.startsWith("http") || data.url.startsWith("mailto:")
      ? data.url
      : `/${locale}/${data.url.replace(/^\//, "")}`
    : null;

  const ctaButton = (
    <Button variant="contained" className={classes.ctaButton} disableElevation>
      {data.buttonText}
    </Button>
  );

  return (
    <Section color={SECTION_NAVY} className={classes.section}>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          {hasText(data.subheadline) && (
            <Typography component="p" className={classes.subheadline}>
              {data.subheadline}
            </Typography>
          )}
          <Typography
            variant="h2"
            component="h2"
            className={[
              classes.headline,
              hasText(data.subheadline) && classes.headlineAfterSubheadline,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {data.title}
          </Typography>
          {hasText(data.description) && (
            <div className={classes.description}>
              <ReactMarkdown
                linkTarget="_blank"
                components={{
                  p: ({ children }) => <p>{children}</p>,
                  strong: ({ children }) => <strong>{children}</strong>,
                  ul: ({ children }) => <ul>{children}</ul>,
                  li: ({ children }) => <li>{children}</li>,
                }}
              >
                {data.description!}
              </ReactMarkdown>
            </div>
          )}
          {data.buttonText && href && (
            href.startsWith("http") || href.startsWith("mailto:") ? (
              <a
                href={href}
                className={classes.ctaLink}
                target={href.startsWith("mailto:") ? undefined : "_blank"}
                rel="noopener noreferrer"
              >
                {ctaButton}
              </a>
            ) : (
              <Link to={href} className={classes.ctaLink}>
                {ctaButton}
              </Link>
            )
          )}
        </Grid>

        {gatsbyImage && (
          <Grid item xs={12} md={6} className={classes.imageOrder}>
            <div className={classes.imagePanel}>
              <GatsbyImage
                image={gatsbyImage}
                alt={data.title || ""}
                className={classes.image}
              />
            </div>
          </Grid>
        )}
      </Grid>
    </Section>
  );
};

export default BottomCtaSection;
